/**
 * ColorPairGroup Component
 *
 * One labelled editing group: a background slot and its foreground slot, with a
 * colour picker and hex field each, plus the contrast verdict for the pair.
 *
 * Grouped by what the pair paints rather than by slot name, because an operator
 * choosing a colour is thinking about the page background, not about `onSurface`.
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 3.8, 4.2, 4.3, 4.4
 */

import { useCallback, useId } from 'react';
import type { BrandColorSlot, BrandColorsDraft } from '../models/domainModels';
import {
  isBrandColor,
  isPartialBrandColor,
  slotLabel,
  type ColorPair,
} from '../utils/brandColors';
import type { ContrastFinding } from '../utils/contrastCheck';
import './ColorPairGroup.css';

/** A neutral swatch for an unset slot, so the control still reads as a colour field. */
const UNSET_SWATCH = '#FFFFFF';

export interface ColorPairGroupProps {
  readonly pair: ColorPair;
  readonly colors: BrandColorsDraft;
  /** The contrast verdict for this pair, absent when neither half is set. */
  readonly finding: ContrastFinding | undefined;
  /** Slots differing from the stored record, for the unsaved marker. */
  readonly changedSlots: ReadonlySet<BrandColorSlot>;
  /** Per-slot messages from a rejected write. */
  readonly slotMessages: ReadonlyMap<BrandColorSlot, string>;
  readonly onChange: (slot: BrandColorSlot, value: string) => void;
}

interface ColorSlotFieldProps {
  readonly slot: BrandColorSlot;
  readonly value: string;
  readonly isChanged: boolean;
  readonly backendMessage: string | undefined;
  readonly onChange: (slot: BrandColorSlot, value: string) => void;
}

/**
 * One slot's controls.
 *
 * Local to this file rather than exported: it is a layout detail of the group,
 * and every caller wants the pair.
 */
function ColorSlotField({
  slot,
  value,
  isChanged,
  backendMessage,
  onChange,
}: ColorSlotFieldProps): JSX.Element {
  const fieldId = useId();
  const label = slotLabel(slot);

  const isUnset = value.length === 0;
  /*
   * Flagged only when the value can no longer become a colour.
   *
   * A prefix like `#0B5` is on its way to being valid, so marking it invalid would
   * put an error under every field the moment it is typed into. Such a value still
   * blocks saving — the page explains that — but it is not an error yet.
   *
   * A definitively wrong value is still reachable without typing: the adapter
   * retains whatever is stored, so a record written before a validation change can
   * load something like `#0B5F3AFF` straight into the field.
   */
  const isInvalid = !isUnset && !isPartialBrandColor(value);

  const handleHexChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value;

      // Clearing the field means "unset", which is how a slot returns to the
      // Toptracer default.
      if (raw.length === 0) {
        onChange(slot, '');
        return;
      }

      const withHash = raw.startsWith('#') ? raw : `#${raw}`;

      // Intermediate values are passed up so the controlled field stays typeable;
      // anything that could never become a colour is dropped instead of stored.
      if (isPartialBrandColor(withHash)) {
        onChange(slot, withHash.toUpperCase());
      }
    },
    [slot, onChange]
  );

  const handlePickerChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(slot, event.target.value.toUpperCase());
    },
    [slot, onChange]
  );

  const messageId = `${fieldId}-message`;
  const message = backendMessage ?? (isInvalid ? 'Must be a six-digit hex colour, like #0B5F3A.' : undefined);

  return (
    <div className="color-slot">
      <div className="color-slot__header">
        <label className="color-slot__label" htmlFor={fieldId}>
          {label}
        </label>
        {isChanged && (
          <span
            className="color-slot__changed"
            title="Unsaved change"
            aria-label="Unsaved change"
          >
            ●
          </span>
        )}
      </div>

      <div className="color-slot__controls">
        <span className="color-slot__picker-wrapper">
          <input
            type="color"
            className="color-slot__picker"
            value={isBrandColor(value) ? value : UNSET_SWATCH}
            onChange={handlePickerChange}
            aria-label={`${label} colour picker`}
          />
          <span
            className={`color-slot__swatch${
              isUnset ? ' color-slot__swatch--unset' : ''
            }`}
            style={isBrandColor(value) ? { backgroundColor: value } : undefined}
            aria-hidden="true"
          />
        </span>

        <input
          id={fieldId}
          type="text"
          className={`color-slot__hex${isInvalid ? ' color-slot__hex--invalid' : ''}`}
          value={value}
          onChange={handleHexChange}
          maxLength={7}
          placeholder="Default"
          spellCheck={false}
          autoComplete="off"
          aria-label={`${label} hex value`}
          aria-invalid={isInvalid || backendMessage !== undefined}
          aria-describedby={message !== undefined ? messageId : undefined}
        />
      </div>

      {message !== undefined && (
        <p id={messageId} className="color-slot__message">
          {message}
        </p>
      )}

      {isUnset && message === undefined && (
        <p className="color-slot__hint">Uses the Toptracer default</p>
      )}
    </div>
  );
}

/** The contrast verdict, rendered only when there is something to say. */
function ContrastWarning({ finding }: { readonly finding: ContrastFinding }): JSX.Element | null {
  if (finding.severity === 'ok') return null;

  const ratio = `${finding.ratio.toFixed(2)}:1`;

  // Below the invisible floor the app substitutes its own default, so the wording
  // has to say the colours will not be used. Without that the operator sees
  // neither their colour nor a reason.
  const text =
    finding.severity === 'will_be_ignored'
      ? `Contrast is only ${ratio}. The app will ignore these colours and use the Toptracer default instead.`
      : `Contrast is ${ratio}, below the WCAG AA minimum of 4.5:1. These colours will be used, but may be hard to read.`;

  const measuredAgainst = finding.comparedAgainstDefault
    ? ' One half of this pair is unset, so it was measured against the Toptracer default.'
    : '';

  return (
    <p
      className={`color-pair__warning color-pair__warning--${finding.severity}`}
      role="alert"
    >
      {text}
      {measuredAgainst}
    </p>
  );
}

export function ColorPairGroup({
  pair,
  colors,
  finding,
  changedSlots,
  slotMessages,
  onChange,
}: ColorPairGroupProps): JSX.Element {
  return (
    <section className="color-pair">
      <h3 className="color-pair__label">{pair.label}</h3>
      <p className="color-pair__description">{pair.description}</p>

      <div className="color-pair__slots">
        {[pair.background, pair.foreground].map((slot) => (
          <ColorSlotField
            key={slot}
            slot={slot}
            value={colors[slot]}
            isChanged={changedSlots.has(slot)}
            backendMessage={slotMessages.get(slot)}
            onChange={onChange}
          />
        ))}
      </div>

      {finding !== undefined && <ContrastWarning finding={finding} />}
    </section>
  );
}
